/**
 * =============================================================================
 * GLOBAL THEME COMPOSABLE
 * =============================================================================
 *
 * Provides global theme state and button class helpers.
 * Persisted preset id lives in `useThemeStore` (persist: true). This composable
 * is a thin wrapper that also applies CSS variables to the document root.
 *
 * Usage in components:
 * ```vue
 * <script setup>
 * import { useTheme } from '@/composables/useTheme';
 * const theme = useTheme();
 * </script>
 *
 * <template>
 *   <button class="btn" :class="theme.btn.primary">Save</button>
 *   <button class="btn" :class="theme.btn.secondary">Export</button>
 * </template>
 * ```
 */

import { ref, computed } from 'vue';
import { THEME_PRESETS } from '@/views/styleguide/data/themePresets';
import { useThemeStore } from '@/stores/theme-store';

// Per-color overrides applied via the styleguide color pickers — not persisted.
const customColors = ref<Record<string, string>>({});

let cssVarsApplied = false;

// Shift a hex colour's HSL lightness by `deltaL` (−1..1), keeping hue and saturation. Used to derive
// dark-mode brand tokens from a preset's brand colour: lightening in HSL (not mixing toward white)
// keeps the blue saturated instead of washing it grey — see applyDarkModeTokens.
function adjustLightness(hex: string, deltaL: number): string {
  const m = /^#?([\da-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  let r = ((n >> 16) & 0xff) / 255;
  let g = ((n >> 8) & 0xff) / 255;
  let b = (n & 0xff) / 255;

  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }

  const nl = Math.min(1, Math.max(0, l + deltaL));
  if (s === 0) {
    r = g = b = nl;
  } else {
    const q = nl < 0.5 ? nl * (1 + s) : nl + s - nl * s;
    const p = 2 * nl - q;
    const hue = (t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    r = hue(h + 1 / 3);
    g = hue(h);
    b = hue(h - 1 / 3);
  }

  const to2 = (v: number) => Math.round(v * 255).toString(16).padStart(2, '0');
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

// Bootstrap's .text-primary / .bg-primary utilities read the comma-separated triplet --bs-primary-rgb
// (so they can layer opacity via rgba()), NOT the --bs-primary hex. Any override of the brand blue
// must set both. Returns "r, g, b".
function hexToRgb(hex: string): string {
  const m = /^#?([\da-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 0xff}, ${(n >> 8) & 0xff}, ${n & 0xff}`;
}

// The colours a preset supplies are the LIGHT-mode brand values. We remember them so the dark-mode
// derivation (applyDarkModeTokens) can recompute whenever the theme toggles between light and dark.
// Seeded from the default preset at module load so applyDarkModeTokens is safe to call before any
// component has invoked useTheme() (e.g. from App.vue's immediate theme watcher on first paint).
let lightModeColors: Record<string, string> =
  THEME_PRESETS.find(p => p.id === 'default')?.colors ?? THEME_PRESETS[0].colors;

function applyCssVariables(colors: Record<string, string>) {
  lightModeColors = colors;
  const root = document.documentElement;
  Object.entries(colors).forEach(([key, value]) => {
    root.style.setProperty(`--bs-${key}`, value);
  });
  applyDarkModeTokens();
}

// The base $primary is tuned for light surfaces and reads too dark on the dark body. This unifies
// EVERY brand-blue surface onto one dark-mode value so there's no "two blues" split: --bs-primary
// drives solid/outline buttons and .text-primary (buttons/form-check are repointed at --bs-primary
// in styles.scss because Bootstrap bakes $primary to a static hex), and --bs-link-color* drives
// links/breadcrumbs (which otherwise use Bootstrap's own #6ea8fe). One knob — DARK_BLUE_LIGHTEN.
//
// These MUST be set inline on <html>, not via a stylesheet rule: applyCssVariables writes the base
// --bs-primary inline, and inline styles beat any [data-bs-theme=dark] stylesheet selector no matter
// its specificity. So we override inline-over-inline (last write wins) while dark mode is active, and
// restore the light base when it isn't. Called on preset change AND on theme toggle (see setThemeMode).
export function applyDarkModeTokens() {
  const root = document.documentElement;
  const isDark = root.getAttribute('data-bs-theme') === 'dark';
  const primary = lightModeColors.primary;

  const emphasisVars = [
    '--bs-primary-text-emphasis',
    '--bs-primary-bg-subtle',
    '--bs-primary-border-subtle',
  ];

  if (!isDark || !primary) {
    // Light mode (or no primary): restore the light base so the utilities read the original hue.
    if (primary) {
      root.style.setProperty('--bs-primary', primary);
      root.style.setProperty('--bs-primary-rgb', hexToRgb(primary));
    } else {
      root.style.removeProperty('--bs-primary');
      root.style.removeProperty('--bs-primary-rgb');
    }
    // Hand the breadcrumb/link colour back to Bootstrap's stylesheet defaults in light mode.
    root.style.removeProperty('--bs-link-color');
    root.style.removeProperty('--bs-link-color-rgb');
    root.style.removeProperty('--bs-link-hover-color-rgb');
    emphasisVars.forEach(v => root.style.removeProperty(v));
    return;
  }

  // Single dark-mode brand-blue brightness knob. 0 = raw brand blue (original darker blue);
  // raise to lighten. Everything blue in dark mode reads --bs-primary, so this one value drives
  // solid buttons, .text-primary, outline buttons (repointed in styles.scss) AND breadcrumb links.
  const DARK_BLUE_LIGHTEN = 0.15;
  const darkPrimary = adjustLightness(primary, DARK_BLUE_LIGHTEN);
  root.style.setProperty('--bs-primary', darkPrimary);
  root.style.setProperty('--bs-primary-rgb', hexToRgb(darkPrimary)); // utilities read the rgb triplet
  // Unify links/breadcrumbs onto the same brand blue (Bootstrap otherwise uses its own #6ea8fe).
  // Links render from the -rgb triplets; on :hover Bootstrap swaps link-color-rgb for the hover
  // triplet, so set both to darkPrimary — hover stays the same blue instead of jumping to a default.
  root.style.setProperty('--bs-link-color', darkPrimary);
  root.style.setProperty('--bs-link-color-rgb', hexToRgb(darkPrimary));
  root.style.setProperty('--bs-link-hover-color-rgb', hexToRgb(darkPrimary));
  root.style.setProperty('--bs-primary-text-emphasis', adjustLightness(primary, 0.30));
  root.style.setProperty('--bs-primary-bg-subtle', adjustLightness(primary, -0.28));
  root.style.setProperty('--bs-primary-border-subtle', adjustLightness(primary, -0.12));
}

export function useTheme() {
  const themeStore = useThemeStore();

  // Apply the persisted preset to CSS vars on first use.
  if (!cssVarsApplied) {
    cssVarsApplied = true;
    const preset = THEME_PRESETS.find(p => p.id === themeStore.activePresetId);
    if (preset) {
      applyCssVariables(preset.colors);
    } else {
      themeStore.setActivePreset('default');
    }
  }

  const activePreset = computed(() =>
    THEME_PRESETS.find(p => p.id === themeStore.activePresetId) || THEME_PRESETS[0]
  );

  const btn = computed(() => ({
    primary: activePreset.value.buttonClasses.primary,
    secondary: activePreset.value.buttonClasses.secondary,
    tertiary: activePreset.value.buttonClasses.tertiary,
    danger: activePreset.value.buttonClasses.danger,
    cancel: activePreset.value.buttonClasses.cancel
  }));

  function applyTheme(presetId: string) {
    const preset = THEME_PRESETS.find(p => p.id === presetId);
    if (preset) {
      themeStore.setActivePreset(presetId);
      applyCssVariables(preset.colors);
    }
  }

  function updateColor(colorKey: string, colorValue: string) {
    customColors.value[colorKey] = colorValue;
    document.documentElement.style.setProperty(`--bs-${colorKey}`, colorValue);
  }

  function resetTheme() {
    applyTheme('default');
    customColors.value = {};
  }

  return {
    activePreset,
    btn,
    applyTheme,
    updateColor,
    resetTheme,
    presets: THEME_PRESETS
  };
}
